import React from 'react';
import Tile from './Tile';
import Timer from './Timer';
import { useTheme } from 'next-themes';
import { GAME_SETTINGS, GameMode } from '@/utils/gameState';
import { useGame } from '@/context/GameContext';
import { LetterEvaluation } from '@/utils/wordUtils';

interface GridProps {
  guesses: string[];
  evaluations: LetterEvaluation[][];
  currentGuess: string;
  revealed: boolean;
  handleTimerTimeout: () => void;
  currentRowIndex: number;
}

const Grid: React.FC<GridProps> = ({ 
  guesses, 
  evaluations, 
  currentGuess, 
  revealed,
  handleTimerTimeout,
  currentRowIndex
}) => {
  const { gameConfig, timerActive } = useGame();
  const { resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === 'dark';

  const { wordLength, maxAttempts } = GAME_SETTINGS;
  
  // Create empty rows to fill the grid
  const emptyRows = Array(maxAttempts).fill('').map(() => 
    Array(wordLength).fill('')
  );

  // Fill in the guesses we have
  const rows = emptyRows.map((row, rowIndex) => {
    // If we have a guess for this row, use it
    if (guesses[rowIndex]) {
      return guesses[rowIndex].split('');
    }
    
    // If this is the current row, fill in what the user has typed so far
    if (rowIndex === guesses.length) {
      const currentRow = [...currentGuess.split(''), ...Array(wordLength - currentGuess.length).fill('')];
      return currentRow;
    }
    
    // Otherwise return the empty row
    return row;
  });

  return (
    <div className="grid gap-1 w-full max-w-[35vh] mx-auto">
      {rows.map((row, rowIndex) => {
        // Determine if this row has been evaluated and should be permanently revealed
        const isPreviouslyRevealedRow = rowIndex < guesses.length - 1;
        
        // Only the current completed row (if any) should be animated during reveal
        const isCurrentRevealRow = rowIndex === guesses.length - 1 && guesses.length > 0;

        const isCurrentGuessRow = rowIndex === guesses.length;
        
        // Whether this row should be revealed at all
        const shouldBeRevealed = (isPreviouslyRevealedRow || isCurrentRevealRow) && revealed;
        
        return (
          <div key={rowIndex} className="relative grid grid-cols-4 gap-1">
              {row.map((letter, colIndex) => {
                // Get the evaluation for this tile if available
                const evaluation = shouldBeRevealed ? evaluations[rowIndex]?.[colIndex] : undefined;
                
                return (
                  <Tile
                    key={colIndex}
                    letter={letter}
                    evaluation={evaluation}
                    revealed={shouldBeRevealed}
                    position={colIndex}
                    animated={isCurrentRevealRow}
                  />
                );
              })}

              {gameConfig.mode === GameMode.ENCORE && timerActive && isCurrentGuessRow && (
                <Timer
                  isActive={timerActive}
                  onTimeout={handleTimerTimeout}
                  className={`absolute right-outside top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'bg-gray-600/30' : 'bg-gray-300/30'} rounded-md px-2 py-1`}
                  style={{'--current-row': currentRowIndex} as React.CSSProperties}
                />
              )}
          </div>
        );
      })}
    </div>
  );
};

export default Grid;
